import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import {
  type TagBulkChargeFamilyPreview,
  type TagBulkChargeMode,
  getTagBulkChargeOutcome,
  useBulkChargeByTags,
} from '~entities/payment/api/bulk-charge-by-tags';
import { useGetTransactionCode } from '~entities/payment/api/get-transaction-codes';
import { useGetV2ModeStatus } from '~entities/v2-mode/api/v2-mode-api';

import { useGetCurrency } from '~shared/api/currency-api';
import { KEYS } from '~shared/api/query-keys';
import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  formatScheduledDate,
  getEarliestScheduledDay,
  parseScheduledDate,
} from '~shared/utils/scheduled-date';

interface TagChargeDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  families: Array<TagBulkChargeFamilyPreview>;
  onComplete: () => void;
}

export function TagChargeDetailsDialog({
  open,
  onOpenChange,
  families,
  onComplete,
}: TagChargeDetailsDialogProps) {
  const navigate = useNavigate();
  const [chargeName, setChargeName] = useState('');
  const [defaultAmount, setDefaultAmount] = useState('');
  const [transactionCodeId, setTransactionCodeId] = useState('');
  const [mode, setMode] = useState<TagBulkChargeMode>('now');
  const [scheduledDates, setScheduledDates] = useState<Array<string>>(['']);
  const [rowAmounts, setRowAmounts] = useState<Record<string, string>>({});

  const { data: currency } = useGetCurrency();
  const { data: transactionCodes } = useGetTransactionCode();
  const chargeMutation = useBulkChargeByTags();
  const { data: v2ModeStatus } = useGetV2ModeStatus();
  const isV2 = v2ModeStatus?.isV2 === true;

  // Init / re-init row amounts only when the dialog opens or the family list changes.
  // Do NOT key off defaultAmount — that wiped per-family edits on every keystroke (Bugbot).
  useEffect(() => {
    if (!open) return;
    setRowAmounts((prev) => {
      const next: Record<string, string> = {};
      for (const family of families) {
        next[family.parentId] = prev[family.parentId] ?? '';
      }
      return next;
    });
  }, [open, families]);

  useEffect(() => {
    if (mode === 'now') setScheduledDates([]);
    else if (mode === 'pay_later') setScheduledDates(['']);
    else setScheduledDates(['', '']);
  }, [mode]);

  const currencySymbol = currency?.symbol ?? '$';

  const filledScheduledDates = useMemo(
    () => scheduledDates.filter((d) => d.trim()),
    [scheduledDates],
  );

  const chargeOccurrences =
    mode === 'now' ? 1 : Math.max(filledScheduledDates.length, 0);

  const familyAmountTotal = useMemo(() => {
    return families.reduce((sum, family) => {
      const value = Number.parseFloat(rowAmounts[family.parentId] || '');
      return sum + (Number.isFinite(value) && value >= 0 ? value : 0);
    }, 0);
  }, [families, rowAmounts]);

  // Installments divide each family's amount across dates, so the run total is
  // the entered total regardless of how many dates are picked.
  const perDateBatchTotal =
    chargeOccurrences > 0 ? familyAmountTotal / chargeOccurrences : 0;

  const applyDefaultAmountToAll = () => {
    const amount = defaultAmount.trim();
    if (!amount || Number.parseFloat(amount) < 0) {
      toast.error('Enter a valid default amount first');
      return;
    }
    const next: Record<string, string> = {};
    for (const family of families) {
      next[family.parentId] = amount;
    }
    setRowAmounts(next);
  };

  const canSubmit = useMemo(() => {
    if (!chargeName.trim()) return false;
    const amountsOk = families.every((family) => {
      const value = Number.parseFloat(rowAmounts[family.parentId] || '');
      return Number.isFinite(value) && value >= 0;
    });
    if (!amountsOk) return false;
    if (mode === 'now') return true;
    const filled = filledScheduledDates;
    if (mode === 'pay_later') return filled.length === 1;
    if (filled.length < 1 || filled.length > 3) return false;
    const days = filled.map((d) => new Date(d).toISOString().slice(0, 10));
    return new Set(days).size === days.length;
  }, [chargeName, families, rowAmounts, mode, filledScheduledDates]);

  const updateScheduledDate = (index: number, value: string | null) => {
    setScheduledDates((prev) => {
      const next = [...prev];
      next[index] = value ?? '';
      return next;
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      toast.error('Fill charge name, amounts, and unique dates');
      return;
    }

    const filledDates = filledScheduledDates;
    chargeMutation.mutate(
      {
        params: {
          families: families.map((family) => ({
            parentId: family.parentId,
            studentId: family.studentId,
            amount: Number.parseFloat(rowAmounts[family.parentId] || '0'),
          })),
          chargeName: chargeName.trim(),
          ...(transactionCodeId ? { transactionCodeId } : {}),
          mode,
          ...(mode !== 'now' ? { scheduledDates: filledDates } : {}),
        },
        isV2,
      },
      {
        onSuccess: (data) => {
          const outcome = getTagBulkChargeOutcome(data);
          if (outcome.type === 'error') {
            toast.error(outcome.message);
          } else if (outcome.type === 'partial') {
            toast.warning(outcome.message);
          } else {
            toast.success(outcome.message);
          }
          onComplete();
          void navigate({ to: '/payment/class-charge-history' });
          void queryClient.invalidateQueries({
            queryKey: [KEYS.BULK_CHARGE_HISTORY],
          });
        },
        onError: () => {
          toast.error('Failed to run tag bulk charge');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-3xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Tag bulk charge — {families.length} families</DialogTitle>
        </DialogHeader>

        <div className='grid gap-4 py-2'>
          <div className='grid gap-2'>
            <Label>Charge name</Label>
            <Input
              value={chargeName}
              onChange={(e) => {
                setChargeName(e.target.value);
              }}
              placeholder='Costume deposit'
            />
          </div>

          <div className='grid gap-2'>
            <Label>Default amount ({currencySymbol})</Label>
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
              <Input
                type='number'
                min='0'
                step='0.01'
                value={defaultAmount}
                onChange={(e) => {
                  setDefaultAmount(e.target.value);
                }}
                placeholder='100.00'
              />
              <Button
                type='button'
                variant='secondary'
                onClick={applyDefaultAmountToAll}
              >
                Apply to all families
              </Button>
            </div>
            <p className='text-xs text-muted-foreground'>
              Set a default, then Apply to all. After that you can still edit any
              family row without losing overrides.
            </p>
          </div>

          <div className='grid gap-2'>
            <Label>Transaction code</Label>
            <Select
              value={transactionCodeId}
              onValueChange={setTransactionCodeId}
            >
              <SelectTrigger>
                <SelectValue placeholder='Optional' />
              </SelectTrigger>
              <SelectContent>
                {(transactionCodes ?? []).map((code) => (
                  <SelectItem key={code._id} value={code._id}>
                    {code.code}
                    {code.description ? ` — ${code.description}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='grid gap-2'>
            <Label>When to charge</Label>
            <Select
              value={mode}
              onValueChange={(value) => {
                setMode(value as TagBulkChargeMode);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='now'>Charge Now</SelectItem>
                <SelectItem value='pay_later'>Pay Later (1 date)</SelectItem>
                <SelectItem value='installments'>
                  Installments (up to 3 dates)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode !== 'now' && (
            <div className='space-y-3 rounded-md border p-3'>
              <p className='text-sm font-medium'>
                Scheduled dates
                {mode === 'installments' ? ' (max 3, unique)' : ''}
              </p>
              {mode === 'installments' && (
                <p className='text-xs text-muted-foreground'>
                  The family amount is split evenly across the dates. Example:{' '}
                  {currencySymbol}300 with 3 dates schedules {currencySymbol}100
                  on each date ({currencySymbol}300 total per family). Cent
                  remainders go to the earliest dates.
                </p>
              )}
              {scheduledDates.map((date, index) => (
                <div key={index} className='flex items-center gap-2'>
                  <DateTimePicker
                    hourCycle={12}
                    granularity='day'
                    displayFormat={{ hour12: 'PPP' }}
                    value={
                      date
                        ? (parseScheduledDate(date) ?? undefined)
                        : undefined
                    }
                    onChange={(next) => {
                      updateScheduledDate(
                        index,
                        next ? formatScheduledDate(next) : null,
                      );
                    }}
                    disabledDays={(day) => day < getEarliestScheduledDay()}
                  />
                  {mode === 'installments' && scheduledDates.length > 1 && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setScheduledDates((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              {mode === 'installments' && scheduledDates.length < 3 && (
                <Button
                  type='button'
                  variant='secondary'
                  size='sm'
                  onClick={() => {
                    setScheduledDates((prev) => [...prev, '']);
                  }}
                >
                  Add date
                </Button>
              )}
            </div>
          )}

          <div className='space-y-2'>
            <Label>Per-family amounts</Label>
            <div className='max-h-64 space-y-2 overflow-y-auto rounded-md border p-2'>
              {families.map((family) => (
                <div
                  key={family.parentId}
                  className='grid gap-1 rounded border p-2 md:grid-cols-[1fr_120px]'
                >
                  <div>
                    <div className='text-sm font-medium'>{family.parentName}</div>
                    <div className='text-xs text-muted-foreground'>
                      {family.studentNames.join(', ')} ·{' '}
                      {family.hasPaymentMethodOnFile
                        ? 'Payment method on file'
                        : 'No payment method → balance due'}
                    </div>
                  </div>
                  <Input
                    type='number'
                    min='0'
                    step='0.01'
                    value={rowAmounts[family.parentId] ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRowAmounts((prev) => ({
                        ...prev,
                        [family.parentId]: value,
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
            {familyAmountTotal > 0 && (
              <p className='text-sm text-muted-foreground'>
                {mode === 'installments' && chargeOccurrences > 1
                  ? `Scheduled total: ${currencySymbol}${familyAmountTotal.toFixed(2)} across ${families.length} families. Each family's amount is split into ${chargeOccurrences} installments — ${currencySymbol}${perDateBatchTotal.toFixed(2)} collected across all families on each date.`
                  : mode === 'pay_later'
                    ? `Scheduled total: ${currencySymbol}${familyAmountTotal.toFixed(2)} across ${families.length} families.`
                    : `Charge total: ${currencySymbol}${familyAmountTotal.toFixed(2)} across ${families.length} families.`}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => {
            onOpenChange(false);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || chargeMutation.isPending}
          >
            {chargeMutation.isPending ? 'Charging...' : 'Submit charges'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
